import React from "react";
import { Helmet } from 'react-helmet-async';
import { Button, Card, CardBody, Divider } from "@heroui/react";
import { motion } from "framer-motion";
import { Icon } from "@iconify/react";
import { Link } from "react-router-dom";
import { useNewsDetail } from "../hooks/use-news";
import { BASE_URL } from "../services/config";
import { useLocation } from 'react-router-dom';
import { Spinner } from "@heroui/react";
import MarkdownContent from "../components/markdown-content";

interface NewsDetailPageProps {
  newsId: number | string;
  onGoBack: () => void;
}

interface NewsDetail {
  id: number;
  title: string;
  content: string | string[];
  date: string;
  image: string;
  category: string;
  author: string;
  relatedNews: RelatedNews[];
}

interface RelatedNews {
  id: number;
  title: string;
  excerpt: string;
  date: string;
  image: string;
  category: string;
}

export const NewsDetailPage: React.FC<NewsDetailPageProps> = ({ newsId, onGoBack }) => {
  // Replace static data with API call via hook
  const { newsDetail, isLoading, error, isApiError } = useNewsDetail(newsId);
  const location = useLocation();

  const getContentText = React.useCallback((content?: string | string[] | null) => {
    if (!content) return "";
    if (Array.isArray(content)) {
      return content.filter(Boolean).join("\n\n");
    }
    return String(content);
  }, []);

  return (
    <section className="py-24 bg-background">
      <div className="container mx-auto px-6">
        {/* Back to News button */}
        <div className="max-w-6xl mx-auto mb-8">
          {/* back button is below */}
          <Button
            variant="flat"
            color="default"
            startContent={<Icon icon="lucide:arrow-left" width={18} />}
            onClick={(e) => {
              e.preventDefault();
              onGoBack();
            }}>
              Назад к новостям
            </Button>
        </div>
        
        {/* NOTE: Removed the generic API fallback banner. Errors and "not found" states are handled below. */}
        
        {/* News Detail Content */}
        <div className="max-w-6xl mx-auto">
          {isLoading ? (
            // Loading state
            <div className="flex flex-col items-center justify-center py-12">
              <Spinner size="lg" color="primary" className="mb-4" />
              <p className="text-foreground/70">Загрузка новости...</p>
            </div>
          ) : newsDetail ? (
            <>
              <Helmet>
                <title>{newsDetail.title ? `${newsDetail.title} — SAFONOVA` : 'Новости — SAFONOVA'}</title>
                <meta name="description" content={getContentText(newsDetail.content).slice(0,160).replace(/\n+/g, ' ')} />
                <link rel="canonical" href={`${BASE_URL || ''}${location?.pathname || `/news-detail/${newsDetail.id}`}`} />
                <meta property="og:type" content="article" />
                <meta property="og:title" content={newsDetail.title} />
                <meta property="og:description" content={getContentText(newsDetail.content).slice(0,160).replace(/\n+/g, ' ')} />
                {newsDetail.image && <meta property="og:image" content={newsDetail.image} />}
                <meta property="og:url" content={`${BASE_URL || ''}${location?.pathname || `/news-detail/${newsDetail.id}`}`} />
              </Helmet>
              <Helmet>
                <script type="application/ld+json">
                  {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "NewsArticle",
                    headline: newsDetail.title,
                    image: newsDetail.image ? [newsDetail.image] : undefined,
                    author: { "@type": "Person", name: newsDetail.author || 'Команда SAFONOVA' },
                    publisher: { "@type": "Organization", name: 'SAFONOVA' },
                    mainEntityOfPage: { "@type": "WebPage", "@id": `${BASE_URL || ''}${location?.pathname || `/news-detail/${newsDetail.id}`}` },
                    description: getContentText(newsDetail.content)
                  })}
                </script>
                <script type="application/ld+json">
                  {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "BreadcrumbList",
                    itemListElement: [
                      {
                        "@type": "ListItem",
                        position: 1,
                        name: "Главная",
                        item: `${BASE_URL || ''}/`
                      },
                      {
                        "@type": "ListItem",
                        position: 2,
                        name: "Новости",
                        item: `${BASE_URL || ''}/news-list`
                      },
                      {
                        "@type": "ListItem",
                        position: 3,
                        name: newsDetail.title,
                        item: `${BASE_URL || ''}${location?.pathname || `/news-detail/${newsDetail.id}`}`
                      }
                    ]
                  })}
                </script>
              </Helmet>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-4">
                  <span className="text-sm text-foreground/70">{newsDetail.date}</span>
                  <span className="text-foreground/50">•</span>
                  <span className="text-sm bg-primary/10 px-3 py-1">{newsDetail.category}</span>
                </div>
                <h1 className="text-3xl md:text-4xl font-light mb-6">{newsDetail.title}</h1>
              </div>

              <div className="mb-12">
                <img 
                  src={newsDetail.image} 
                  alt={newsDetail.title} 
                  className="w-full h-auto object-cover mb-8"
                />

                {/*
                  News content comes from the backend as plain text blocks (array or string).
                  Per request we do NOT render HTML — render each block as plain text.
                */}
                <MarkdownContent content={getContentText(newsDetail.content)} className="text-lg text-foreground/90" />
              </div>

              {/* Only show the divider if we have related news */}
              {newsDetail.relatedNews && newsDetail.relatedNews.length > 0 && (
                <Divider className="my-12" />
              )}

              {/* Related News */}
              {newsDetail.relatedNews && newsDetail.relatedNews.length > 0 && (
                <div className="mt-16">
                  <h2 className="text-2xl font-light mb-8">Вам может быть <span className="font-medium">интересно</span></h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    {newsDetail.relatedNews.map(news => (
                      <Card 
                        key={news.id}
                        className="border border-divider bg-content2/50 product-card-hover" 
                        isPressable
                        onPress={() => {
                            // In a real app, you would navigate to the related news
                            window.location.href = `/news-detail/${news.slug ?? news.id}`;
                          }}
                        disableRipple
                      >
                        <CardBody className="p-0">
                          <div className="flex flex-col md:flex-row h-full">
                            <div className="md:w-1/3 h-48 md:h-auto">
                              <img 
                                src={news.image} 
                                alt={news.title}
                                className="w-full h-full object-cover" 
                              />
                            </div>
                            <div className="p-6 md:w-2/3">
                              <p className="text-xs text-foreground/60 mb-1">{news.date}</p>
                              <h3 className="text-lg font-medium mb-2">{news.title}</h3>
                              <p className="text-foreground/70 text-sm mb-4 line-clamp-2">{news.excerpt}</p>
                              
                              <Link 
                                className="text-primary hover:text-primary-600 hover:underline flex items-center gap-1 transition-all duration-200"
                                href={`/news-detail/${news.slug ?? news.id}`}
                              >
                                Читать далее <Icon icon="lucide:arrow-right" width={16} />
                              </Link>
                            </div>
                          </div>
                        </CardBody>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
            </>
          ) : (
            // Error state - news not found
            <div className="text-center py-20">
              <Icon icon="lucide:file-x" className="mx-auto mb-4 text-foreground/40" width={64} />
              <h2 className="text-2xl font-medium mb-2">Новость не найдена</h2>
              <p className="text-foreground/70 mb-8">
                Запрашиваемая новость не существует или была удалена
              </p>
              <Button 
                color="primary"
                variant="flat"
                onPress={onGoBack}
                startContent={<Icon icon="lucide:arrow-left" width={16} />}
              >
                К списку новостей
              </Button>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};